import FetchWrapper from '../../shared/utils/FetchWrapper';
import { API_BASE_URL, API_ENDPOINTS } from '../../shared/constants/api';
import { workspaceService } from '../../api/services';
import EventsService from './EventsService';
import QRService from './QRService';

class AttendanceService {
  constructor() {
    this.api = new FetchWrapper(API_BASE_URL);
  }

  async getAttendanceList(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `${API_ENDPOINTS.ATTENDANCE.LIST}?${queryString}` : API_ENDPOINTS.ATTENDANCE.LIST;
    return await this.api.get(endpoint);
  }

  async createAttendance(attendanceData) {
    return await this.api.post(API_ENDPOINTS.ATTENDANCE.CREATE, attendanceData);
  }

  async getAttendanceStats(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `${API_ENDPOINTS.ATTENDANCE.STATS}?${queryString}` : API_ENDPOINTS.ATTENDANCE.STATS;
    return await this.api.get(endpoint);
  }

  async validateAndGenerateCredentials(eventId, eventDetails, userEmail) {
    try {
      const attendeesResponse = await workspaceService.getEventAttendees(eventId, userEmail);
      
      // Verificar si la respuesta tiene el formato esperado
      if (!attendeesResponse.data) {
        throw new Error('Respuesta inválida del servidor');
      }
      
      // Verificar si hay un error en la respuesta
      if (attendeesResponse.data.success === false) {
        let errorMsg = attendeesResponse.data.message || 'Error al obtener participantes';
        // Simplificar mensaje de evento no encontrado
        if (errorMsg.includes('no encontrado en el calendario')) {
          errorMsg = 'Evento no encontrado en el calendario';
        }
        throw new Error(errorMsg);
      }
      
      const attendeesData = attendeesResponse.data.data?.attendees || attendeesResponse.data.attendees || [];
      
      // Si no hay invitados, lanzar error inmediatamente
      if (!attendeesData || attendeesData.length === 0) {
        throw new Error('Este evento no tiene invitados registrados en Google Calendar.');
      }
      
      const invitados = attendeesData.map(attendee => ({
        email: attendee.email,
        displayName: attendee.displayName || attendee.email
      }));

      const validationResult = await EventsService.validateEventGuests(eventId, invitados);
      
      if (!validationResult.success) {
        // Extraer mensaje específico del error
        throw new Error(validationResult.message || 'Error en la validación');
      }

      const eventDbId = await EventsService.getOrCreateEvent(eventId, eventDetails);
      
      if (!eventDbId) {
        throw new Error('No se pudo obtener el ID del evento en la base de datos');
      }

      const credentials = await QRService.generateBothCredentials(eventDbId, eventDetails.title || eventDetails.summary);
      
      return {
        validation: {
          success: true,
          data: validationResult.data,
          invitados: validationResult.data.invitados || invitados,
          totalInvitados: validationResult.data.invitados?.length || invitados.length
        },
        credentials
      };
    } catch (error) {
      // Extraer mensaje del error de axios si existe
      if (error.response?.data) {
        const errorData = error.response.data;
        let errorMessage = errorData.message || errorData.error || error.message;
        // Simplificar mensaje de evento no encontrado
        if (errorMessage.includes('no encontrado en el calendario')) {
          errorMessage = 'Evento no encontrado en el calendario';
        }
        throw new Error(errorMessage);
      }
      // Simplificar mensaje si viene en el error normal
      let errorMessage = error.message;
      if (errorMessage.includes('no encontrado en el calendario')) {
        errorMessage = 'Evento no encontrado en el calendario';
      }
      throw new Error(errorMessage);
    }
  }

  async generateAttendanceLink(eventId, eventDetails, methods) {
    const eventDbId = await EventsService.getOrCreateEvent(eventId, eventDetails);
    
    if (!eventDbId) {
      throw new Error('No se pudo obtener el ID del evento en la base de datos');
    }
    
    return await QRService.generateLinkCredentials(eventDbId, eventDetails.title, methods);
  }

  async generateAttendanceQR(eventId, eventDetails, methods) {
    const eventDbId = await EventsService.getOrCreateEvent(eventId, eventDetails);
    
    if (!eventDbId) {
      throw new Error('No se pudo obtener el ID del evento en la base de datos');
    }
    
    return await QRService.generateQRCredentials(eventDbId, eventDetails.title, methods);
  }
}

export default new AttendanceService();